import { decodeQRBytes } from "./qr";

/**
 * The camera loop. Grabs frames, hands any QR payload it finds to `onPayload`,
 * and returns a function that stops the camera.
 *
 * Safari implements no `BarcodeDetector`, on iOS or macOS, so the WebAssembly
 * reader is the floor rather than an optimisation and the native API would only
 * ever be a fast path where it exists.
 *
 * The decode goes through `qr.ts` and not straight to `zxing-wasm/reader`,
 * which exports the same function. `qr.ts` is where `prepareZXingModule`
 * overrides `locateFile` to the bundled same-origin `.wasm`, and that override
 * is module-level state set when `qr.ts` is first evaluated. Reaching for the
 * library directly from here would decode correctly in the app — `card.ts`
 * pulls `qr.ts` in — and fetch 950 KB from a CDN in any build where the
 * scanner loads first, which is the one thing a verifier in a corridor cannot
 * do. One door, like `crypto.ts` is the one door to `importKey`.
 */
export async function startScanner(
  video: HTMLVideoElement,
  onPayload: (bytes: Uint8Array) => void,
): Promise<() => void> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: "environment" },
  });
  video.srcObject = stream;
  await video.play();

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true })!;
  let running = true;

  const tick = async () => {
    if (!running) return;
    if (video.videoWidth > 0) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const payload = await decodeQRBytes(
        context.getImageData(0, 0, canvas.width, canvas.height),
      );
      // Re-checked after the await: `stop()` may have run while the decoder
      // was working, and a payload delivered after that would verify against a
      // screen the caller has already torn down.
      if (payload && running) onPayload(payload);
    }
    requestAnimationFrame(() => void tick());
  };
  void tick();

  return () => {
    running = false;
    for (const track of stream.getTracks()) track.stop();
    video.srcObject = null;
  };
}
